import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';
import packageJson from "../package.json";

/** Canale Datadog delle interazioni Liquidate: stessa RUM di overx-sinistri, `service` suo. */
export const LIQUIDATE_SERVICE = 'revo---liquidate';
export const LIQUIDATE_SOURCE = 'liquidate';

/**
 * Toglie la query string da un URL. I nostri path portano numeri di sinistro e id di
 * pratica come parametri: finirebbero in chiaro in viste, referrer e risorse, dove il
 * mascheramento del replay non arriva. Il path resta intero perche' serve a raggruppare.
 */
function senzaQueryString(url) {
    if (typeof url !== 'string') return url;
    const taglio = url.indexOf('?');
    return taglio === -1 ? url : url.slice(0, taglio);
}

/**
 * Mascheratore dei path di @revo/liquidate, INIETTATO e non importato qui.
 *
 * Importare la libreria in questo modulo la trascinerebbe nel chunk d'ingresso (porta
 * dentro anche il visualizzatore PDF) e romperebbe i test: il suo bundle tocca
 * `DOMMatrix` all'import, che in jsdom non esiste. Lo registra chi la libreria la carica
 * gia'; nessuna chiamata di liquidate puo' precedere il caricamento di liquidate. [CSP-3310]
 */
let mascheraPathLiquidate = null;
export function setLiquidateUrlScrubber(fn) {
    mascheraPathLiquidate = typeof fn === 'function' ? fn : null;
}

/**
 * Query string via, poi i path della libreria ridotti a pattern: i suoi identificativi
 * stanno NEL PERCORSO — sinistro, task, incarico, codice operatore — dove togliere la
 * query non arriva, e con `trackResources` acceso finirebbero a Datadog in chiaro.
 */
function ripulisciUrl(url) {
    if (typeof url !== 'string') return url;
    const senzaParametri = senzaQueryString(url);
    return mascheraPathLiquidate ? mascheraPathLiquidate(senzaParametri) : senzaParametri;
}

/** Ripulisce dagli URL dell'evento tutto cio' che puo' portare parametri. */
export function scrubUrls(event) {
    if (event?.view) {
        event.view.url = ripulisciUrl(event.view.url);
        event.view.referrer = ripulisciUrl(event.view.referrer);
    }
    if (event?.resource) event.resource.url = ripulisciUrl(event.resource.url);
    if (event?.error?.resource) event.error.resource.url = ripulisciUrl(event.error.resource.url);
    return event;
}

/**
 * Sposta sul canale Liquidate le action marcate da `sendLiquidateTelemetry`, con la versione
 * della libreria (iniettata da craco.config.js) al posto di quella di os-webapp.
 * Le sessioni restano quelle di os-webapp; nessun evento viene scartato.
 */
export function routeLiquidateActions(event) {
    if (event?.type === 'action' && event.context?.source === LIQUIDATE_SOURCE) {
        event.service = LIQUIDATE_SERVICE;
        const version = process.env.REACT_APP_LIQUIDATE_VERSION;
        if (version) event.version = version;
    }
    return true;
}

/**
 * Unico `beforeSend`: prima si ripuliscono gli URL, poi si instrada il canale Liquidate.
 * L'ordine conta solo per leggibilita' — sono indipendenti — ma il valore di ritorno no:
 * deve restare quello di `routeLiquidateActions`, che e' sempre `true` (non si scarta
 * nessun evento).
 */
export function beforeSend(event) {
    scrubUrls(event);
    return routeLiquidateActions(event);
}

// Sessioni anonime: nessun `setUser`. L'SDK conta gli utenti distinti con il suo
// `anonymous_id` (`trackAnonymousUser`), senza identita'.
if (process.env.REACT_APP_DATADOG_ENV === 'uat' || process.env.REACT_APP_DATADOG_ENV === 'prod') {
    datadogRum.init({
        applicationId: '92ed81be-cf1d-45ae-83b4-3e0f2f1a6b35',
        clientToken: 'pub734f80355566e669f5face81d7f49ced',
        site: 'datadoghq.eu',
        service: 'revo---overx-sinistri',
        env: process.env.REACT_APP_DATADOG_ENV,
        version: packageJson.version,
        sessionSampleRate: 100,
        // 20% di replay: le sessioni restano tutte campionate, si registra una su cinque.
        sessionReplaySampleRate: 20,
        // `mask` e non `mask-user-input`: qui il contenuto a schermo e' tanto sensibile
        // quanto quello digitato — numeri di sinistro, anagrafiche, note, documenti. La
        // cornice statica viene riaperta caso per caso con `data-dd-privacy="allow"`.
        defaultPrivacyLevel: 'mask',
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        // Indispensabile insieme a `trackUserInteractions`: senza, Datadog ricava il nome
        // dell'azione dal testo del DOM, che qui porta numeri di sinistro e nomi di parti.
        // Con questo attivo usa solo `data-dd-action-name`, che la libreria gia' espone.
        enablePrivacyForActionName: true,
        trackAnonymousUser: true,
        // Viste per PATTERN di rotta (`/task/:id`) invece che per URL concreto: senza,
        // ogni sinistro sarebbe una vista a se' e i numeri non si aggregherebbero.
        plugins: [reactPlugin({ router: true })],
        beforeSend
    });
}

/**
 * Forza il replay della sessione all'apertura del dettaglio di un task, anche nelle
 * sessioni fuori dal 20% campionato. Solo in prod: in UAT resta il campionamento.
 * Il resto della configurazione (20%, `mask`, `beforeSend`) non cambia. [CSP-3676]
 *
 * Chiamarla a ogni apertura e' innocuo (SDK 6.33.0, `postStartStrategy`): con la
 * registrazione gia' avviata o in avvio la chiamata esce senza fare nulla; una sessione
 * gia' campionata registra come sempre, senza forzatura. Alla scadenza della sessione
 * l'SDK ferma la registrazione e la riprende la prossima apertura di un task.
 */
export function forceSessionReplayRecording() {
    if (process.env.REACT_APP_DATADOG_ENV !== 'prod') return;

    try {
        datadogRum.startSessionReplayRecording({ force: true });
    } catch (e) {
        // Il replay non deve mai disturbare il flusso operativo.
        // eslint-disable-next-line no-console
        console.debug('Datadog startSessionReplayRecording failed', e);
    }
}

/**
 * Instrada sulla RUM la telemetria della libreria @revo/liquidate (ODS-4549).
 *
 * `addAction` NON dipende da `trackUserInteractions`: nel SDK installato
 * `startActionCollection` mette dietro quel flag solo `trackClickActions`, mentre
 * `trackManualActions` parte sempre. Quindi questi eventi arrivano comunque.
 *
 * Dal 2026-10-04 la raccolta automatica dei click e' ACCESA (prima era spenta proprio
 * perche' deduceva i nomi dal DOM): con `enablePrivacyForActionName` Datadog smette di
 * leggere il testo e usa solo `data-dd-action-name`, che la libreria espone con lo stesso
 * id di questi eventi manuali. I due canali quindi non si contraddicono. [CSP-3310]
 *
 * Il nome dell'azione e' l'id stabile della libreria (`<area>.<element>.<verb>`), non testo
 * tradotto: cambiando una traduzione la metrica non si spezza. `source` marca l'evento per
 * `routeLiquidateActions` e va per ultimo, cosi' il contesto non puo' sovrascriverlo.
 */
export function sendLiquidateTelemetry(event) {
    if (!event?.id) return;
    if (!(process.env.REACT_APP_DATADOG_ENV === 'uat' || process.env.REACT_APP_DATADOG_ENV === 'prod')) return;

    try {
        datadogRum.addAction(event.id, {
            area: event.area,
            element: event.element,
            verb: event.verb,
            ...(event.context ?? {}),
            source: LIQUIDATE_SOURCE,
        });
    } catch (e) {
        // La telemetria non deve mai disturbare il flusso operativo.
        // eslint-disable-next-line no-console
        console.debug('Datadog addAction failed', e);
    }
}
